import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Navigation, Clock, ExternalLink, Loader2, X, Home } from 'lucide-react';
import api from '../lib/api';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const AddressSearchInput = ({
  value = '',
  onChange,
  onLocationSelect,
  placeholder = 'Search for an address...',
  homeAddress = null,
  eventStartTime = null,
  className = '',
  showTravelTime = true,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [travelTime, setTravelTime] = useState(null);
  const [loadingTravelTime, setLoadingTravelTime] = useState(false);
  
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  
  const debouncedSearch = useDebounce(inputValue, 300);
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Search for addresses
  useEffect(() => {
    const searchAddresses = async () => {
      if (!debouncedSearch || debouncedSearch.length < 3) {
        setSuggestions([]);
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await api.get('/geocode/search', { params: { q: debouncedSearch } });
        setSuggestions(response.data.results || []);
      } catch (error) {
        console.error('Address search failed:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    searchAddresses();
  }, [debouncedSearch]);
  
  // Calculate travel time when location changes
  useEffect(() => {
    const calculateTravelTime = async () => {
      if (!showTravelTime || !homeAddress?.lat || !selectedLocation?.lat) {
        setTravelTime(null);
        return;
      }
      
      setLoadingTravelTime(true);
      try {
        const response = await api.get('/geocode/travel-time', {
          params: {
            from_lat: homeAddress.lat,
            from_lon: homeAddress.lon,
            to_lat: selectedLocation.lat,
            to_lon: selectedLocation.lon,
            departure_time: eventStartTime || null
          }
        });
        setTravelTime(response.data);
      } catch (error) {
        console.error('Travel time calculation failed:', error);
        setTravelTime(null);
      } finally {
        setLoadingTravelTime(false);
      }
    };
    
    calculateTravelTime();
  }, [selectedLocation, homeAddress, eventStartTime, showTravelTime]);
  
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);
    setSelectedLocation(null);
    setTravelTime(null);
    onChange?.(newValue, null);
  };
  
  const handleSuggestionClick = (suggestion) => {
    const displayName = suggestion.display_name;
    setInputValue(displayName);
    setSelectedLocation({
      lat: suggestion.lat,
      lon: suggestion.lon,
      display_name: displayName
    });
    setSuggestions([]);
    setShowSuggestions(false);
    
    onChange?.(displayName, {
      lat: suggestion.lat,
      lon: suggestion.lon
    });
    onLocationSelect?.(suggestion);
  };
  
  const clearLocation = () => {
    setInputValue('');
    setSelectedLocation(null);
    setTravelTime(null);
    setSuggestions([]);
    onChange?.('', null);
    inputRef.current?.focus();
  };
  
  // Open in maps app
  const openInMaps = () => {
    if (!selectedLocation) return;
    
    const { lat, lon } = selectedLocation;
    const address = encodeURIComponent(inputValue);
    
    // Try to detect mobile and open appropriate app
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // On mobile, use geo: URI which will prompt user to choose maps app
      // Fallback to Google Maps for wider compatibility
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&destination_place_id=${address}`;
      window.open(googleMapsUrl, '_blank');
    } else {
      // On desktop, open Google Maps
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
      window.open(googleMapsUrl, '_blank');
    }
  };
  
  // Format address for display (shorter version)
  const formatShortAddress = (displayName) => {
    if (!displayName) return '';
    const parts = displayName.split(',').slice(0, 3);
    return parts.join(',');
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="h-11 rounded-xl pl-10 pr-10"
          data-testid="address-search-input"
        />
        {inputValue && (
          <button
            type="button"
            onClick={clearLocation}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-4 w-4 text-gray-400" />
          </button>
        )}
        {isLoading && (
          <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>
      
      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100 last:border-0"
              data-testid={`address-suggestion-${index}`}
            >
              <MapPin className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {formatShortAddress(suggestion.display_name)}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {suggestion.display_name}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* Selected Location Info */}
      {selectedLocation && (
        <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                  {formatShortAddress(inputValue)}
                </p>
                {/* Travel Time Display */}
                {showTravelTime && homeAddress?.lat && (
                  <div className="flex items-center gap-2 mt-0.5">
                    {loadingTravelTime ? (
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Calculating...
                      </span>
                    ) : travelTime ? (
                      <span className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                        <Clock className="h-3 w-3" />
                        {travelTime.formatted} from home
                        {travelTime.is_rush_hour && (
                          <span className="text-orange-500">(rush hour)</span>
                        )}
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            
            {/* Open in Maps Button */}
            <button
              type="button"
              onClick={openInMaps}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
              data-testid="open-in-maps-btn"
            >
              <Navigation className="h-3 w-3" />
              Directions
              <ExternalLink className="h-3 w-3" />
            </button>
          </div>
          
          {/* No Home Address Warning */}
          {showTravelTime && !homeAddress?.lat && (
            <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-2 py-1.5 rounded-lg">
              <Home className="h-3 w-3" />
              <span>Set your home address in Settings to see travel times</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressSearchInput;
